/* eslint-disable eslint-comments/no-restricted-disable -- Central URL registry keeps every paywalls endpoint in one place. */
import { absolutePathWithArrayIndices } from "../absolutePath";
import type { AnalyticsApiParams } from "./types";

const subscriptionsPath = "analytics/paywalls/subscriptions";
const subscriptionPath = (endpoint: string, params: AnalyticsApiParams) =>
  absolutePathWithArrayIndices(`${subscriptionsPath}/${endpoint}`, params);

export const paywalls = {
  overview: {
    total_transactions: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/total_transactions",
        params,
      ),
    total_revenue: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/total_revenue",
        params,
      ),
    sales_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/sales_breakdown",
        params,
      ),
    revenue_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/revenue_breakdown",
        params,
      ),
    gross_revenue_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/gross_revenue_breakdown",
        params,
      ),
    transaction_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/transaction_breakdown",
        params,
      ),
    refund_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/refund_breakdown",
        params,
      ),
    refund_rate_breakdown: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/refund_rate_breakdown",
        params,
      ),
    revenue_growth: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/revenue_growth",
        params,
      ),
    highest_grossing_paywalls: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/overview/highest_grossing_paywalls",
        params,
      ),
  },
  upsells: {
    checkout_revenue: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/checkout_revenue",
        params,
      ),
    checkout_revenue_lift: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/checkout_revenue_lift",
        params,
      ),
    lifetime_revenue: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/lifetime_revenue",
        params,
      ),
    lifetime_revenue_lift: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/lifetime_revenue_lift",
        params,
      ),
    views: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices("analytics/paywalls/upsells/views", params),
    conversions: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/conversions",
        params,
      ),
    declines: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/declines",
        params,
      ),
    abandons: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/abandons",
        params,
      ),
    conversion_rate: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/conversion_rate",
        params,
      ),
    median_time_to_convert: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/median_time_to_convert",
        params,
      ),
    conversion_details: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/conversion_details",
        params,
      ),
    revenue_details: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/upsells/revenue_details",
        params,
      ),
  },
  one_time: {
    total_transactions: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/one_time/total_transactions",
        params,
      ),
    total_revenue: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/one_time/total_revenue",
        params,
      ),
    revenue_growth: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/one_time/revenue_growth",
        params,
      ),
    average_order_value: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/one_time/average_order_value",
        params,
      ),
    repurchases: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/one_time/repurchases",
        params,
      ),
  },
  installments: {
    installment_revenue: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/installments/installment_revenue",
        params,
      ),
    active_installments: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/installments/active_installments",
        params,
      ),
    installments_revenue_growth: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/installments/installments_revenue_growth",
        params,
      ),
  },
  subscriptions: {
    monthly_recurring_revenue: (params: AnalyticsApiParams) =>
      subscriptionPath("monthly_recurring_revenue", params),
    annual_recurring_revenue: (params: AnalyticsApiParams) =>
      subscriptionPath("annual_recurring_revenue", params),
    active_subscribers_count: (params: AnalyticsApiParams) =>
      subscriptionPath("active_subscribers_count", params),
    net_monthly_recurring_revenue_growth: (params: AnalyticsApiParams) =>
      subscriptionPath("net_monthly_recurring_revenue_growth", params),
    new_subscribers_count: (params: AnalyticsApiParams) =>
      subscriptionPath("new_subscribers_count", params),
    active_trials_count: (params: AnalyticsApiParams) =>
      subscriptionPath("active_trials_count", params),
    new_trials_count: (params: AnalyticsApiParams) =>
      subscriptionPath("new_trials_count", params),
    converted_trials_count: (params: AnalyticsApiParams) =>
      subscriptionPath("converted_trials_count", params),
    trial_conversion: (params: AnalyticsApiParams) =>
      subscriptionPath("trial_conversion", params),
    trial_conversion_rate_by_date: (params: AnalyticsApiParams) =>
      subscriptionPath("trial_conversion_rate_by_date", params),
    net_subscribers_growth: (params: AnalyticsApiParams) =>
      subscriptionPath("net_subscribers_growth", params),
    average_monthly_revenue: (params: AnalyticsApiParams) =>
      subscriptionPath("average_monthly_revenue", params),
    canceled_subscriptions: (params: AnalyticsApiParams) =>
      subscriptionPath("canceled_subscriptions", params),
    churn_rate: (params: AnalyticsApiParams) =>
      subscriptionPath("churn_rate", params),
    churn_rate_by_date: (params: AnalyticsApiParams) =>
      subscriptionPath("churn_rate_by_date", params),
    monthly_recurring_revenue_growth: (params: AnalyticsApiParams) =>
      subscriptionPath("monthly_recurring_revenue_growth", params),
    active_subscriptions_growth: (params: AnalyticsApiParams) =>
      subscriptionPath("active_subscriptions_growth", params),
    gross_subscription_revenue: (params: AnalyticsApiParams) =>
      subscriptionPath("gross_subscription_revenue", params),
    subscription_refunds: (params: AnalyticsApiParams) =>
      subscriptionPath("subscription_refunds", params),
    net_subscription_revenue: (params: AnalyticsApiParams) =>
      subscriptionPath("net_subscription_revenue", params),
    subscription_revenue_growth: (params: AnalyticsApiParams) =>
      subscriptionPath("subscription_revenue_growth", params),
  },
  conversions: {
    rate_histogram: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/rate_histogram",
        params,
      ),
    number_of_page_views: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/number_of_page_views",
        params,
      ),
    number_of_conversions: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/number_of_conversions",
        params,
      ),
    conversion_rate: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/conversion_rate",
        params,
      ),
    number_of_first_time_visitors: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/number_of_first_time_visitors",
        params,
      ),
    number_of_repeat_visitors: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/number_of_repeat_visitors",
        params,
      ),
    median_time_to_convert: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/median_time_to_convert",
        params,
      ),
    devices: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/devices",
        params,
      ),
    browsers: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/browsers",
        params,
      ),
    bounce_rate: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/bounce_rate",
        params,
      ),
    referral_uris: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/referral_uris",
        params,
      ),
    views_per_country: (params: AnalyticsApiParams) =>
      absolutePathWithArrayIndices(
        "analytics/paywalls/conversions/views_per_country",
        params,
      ),
  },
};
